import React, { Fragment, useEffect, useState } from 'react';

import Head from '../../components/head';
import Header from '../../components/dashboard/header';
import SideNav from '../../components/dashboard/side-navbar';
import DeleteConfirmModal from "../../components/dashboard/delete-confirm";

import useRequest from '../../hooks/useRequest';
import { useContextData } from "../../store/ContextData";
import { commonFunction } from '../../components/common-functions';

import refresh from "../../static/svg/ring-alt.svg"
import warning from "../../static/images/warning.png";
import { downloadTemplate } from '../../utils';

require("../../static/css/dashboard.css");

const { getIcon, getDateFormat, getFileSize, formatDate, showAlert, copyText } = commonFunction;

const ParagraphVersion = (props) => {
  const {match} = props;
  const {params: {paragraphId}} = match;

  const { nanobar } = useContextData();
  const [buttonId, setButtonId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [allVersions, setAllVersion] = useState([]);
  const [paragraphInfo, setParagraphInfo] = useState({});
  const [disabledButton, setDisabledButton] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [versionDataLoading, setVersionDataLoading] = useState(true);

  const {data: paragraphData, loading: paragraphLoading} = useRequest(`api/v1/paragraph/${paragraphId}`);
  useEffect(() => {
    if(paragraphData && !paragraphLoading){
      const {paragraphs} = paragraphData;
      if(paragraphs && paragraphs.length > 0){
        const [info] = paragraphs;
        setParagraphInfo(info);
        setIsLoading(false);
      }
    }
  }, [paragraphData, paragraphLoading]);
  
  const {
    data: versionData, loading: versionLoading, callAPI: callVersionAPI
  } = useRequest(`api/v1/paragraph/${paragraphId}/version`);
  useEffect(() => {
    if(versionData && !versionLoading && !isLoading){
      const { paragraphs } = versionData;
      const allVersions = [...paragraphs];
      setAllVersion(allVersions);
      setVersionDataLoading(false);
    }
  }, [versionData, versionLoading, isLoading]);
  
  const {
    data: activateVersionData, loading: activateVersionLoading, callAPI: callActivateVersionAPI, 
    error: activateError, errorMessage: activateErrorMessage
  } = useRequest(`api/v1/paragraph/${paragraphId}/version/${selectedVersionId}/setActive`, false, {}, "POST")
  useEffect(() => {
    if(selectedVersionId){
      if(activateVersionData && !activateVersionLoading){
        const currentVersion = allVersions.find(version => version._id === selectedVersionId);
        const {versionMetadata: curVerMetaData} = currentVersion;
        setAllVersion(versions => {
          return [...versions].map(version => {
            const {versionMetadata} = version;
            versionMetadata.active = (version._id === selectedVersionId);
            
            return {
              ...version,
              versionMetadata
            };
          });
        });
        setSelectedVersionId("");
        showAlert(`Version ${curVerMetaData.version} has been set as active.`);
      }
      
      if(activateError && activateErrorMessage){
        showAlert(activateErrorMessage, "error");
      }
    }
  }, [
    allVersions, selectedVersionId, activateVersionData, activateVersionLoading, activateError, activateErrorMessage
  ]);

  const {
    data: deleteData, loading: deleteLoading, callAPI: callDeleteAPI
  } = useRequest(`api/v1/paragraph/${paragraphId}/version/${selectedVersionId}/`, false, {}, "DELETE");
  useEffect(() => {
    if(selectedVersionId && deleteData && !deleteLoading){
      const currentVersion = allVersions.find(version => version._id === selectedVersionId);
      const {versionMetadata: curVerMetaData} = currentVersion;
      setSelectedVersionId("");
      setVersionDataLoading(true);
      showAlert(`Version ${curVerMetaData.version} has been deleted successfully.`);
      callVersionAPI();
    }
  }, [selectedVersionId, allVersions, deleteData, deleteLoading, callVersionAPI]);
  
  const { filename, name, addedAt, size } = paragraphInfo;
  const displayName = name || filename;

  return (
    <div>
      <Head customTitle={"Paragraph Version"}/>
      <Header></Header>
      <SideNav name={"paragraph"}/>
      <main className="dashboard-container template">
        <div className="main-container">
          {isLoading && 
            <div className="public-form">
              <div className="loading-screen">
                <div className="flex-container vertical-middle full-height">
                  <div className="flexbox text-center">
                    <div className="loading">
                      <img src={refresh} className="loading" alt="loading"/>
                      <span id="loading" className="text-left">Loading...</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
          {!isLoading && (
            <Fragment>
              <div className="template-details">
                <div className="icon-container">
                  <img src={getIcon(`${name}.txt`)} className="icon" alt="paragraph type"/>
                </div>
                <div className="name-size-block">
                  <h1 className="name">{displayName}</h1>
                  {filename && filename !== displayName &&
                    <div className="key-value">
                      <div className="key">File</div>
                      <div className="value">{filename}</div>
                    </div>
                  }
                  <div className="date-size">
                    <span className="text-uppercase">{getDateFormat(addedAt)}</span>
                    {size &&
                      <Fragment>
                        <span className="separator">-</span>
                        <span className="text-uppercase">{getFileSize(size)}</span>
                      </Fragment>
                    }
                  </div>
                </div>
              </div>
              <h2 className="heading">All versions</h2>
              <h4 className="sub-heading">Find all available versions of the uploaded paragraph.</h4>
              {!versionDataLoading &&
                <ul className="timeline-block">
                  {
                    allVersions.map(version => {
                      const { 
                        _id, versionMetadata: {active, version: versionNum}, 
                        birthtime
                      } = version;

                      return (
                        <li className={active ? "current-version" : ""} key={_id}>
                          <div className="time text-uppercase">{formatDate(new Date(birthtime))}</div>
                          <span className="dot"></span>
                          <div className="flex-container with-gap no-margin vertical-middle flex-start">
                            <div className="title">
                              {versionNum === 0 ? "Initial version" : `Version ${versionNum}`}
                            </div>
                            <button
                              type="button"
                              className={`status-pill${active ? " approved" : ""}`}
                              title={`Copy ID: ${_id}`}
                              onClick={() => {
                                copyText(_id);
                                showAlert("Version ID has been successfully copied.");
                              }}
                            >
                              {_id}
                            </button>
                          </div>

                          <div className={`buttons ${buttonId === _id ? "open" : ""}`}>
                            <button
                              className="btn bullet"
                              onClick={() => setButtonId(id => (id && id === _id) ? "" : _id)}
                            >
                              <span>&bull;&bull;&bull;</span>
                            </button>
                            <div className="actions">
                              <ul>
                                {!active && 
                                  <li onClick={() => {
                                    setButtonId("");
                                    setSelectedVersionId(_id);
                                    setTimeout(() => {
                                      callActivateVersionAPI();
                                    });
                                  }}>Mark as current</li>
                                }
                                <li onClick={() => {
                                  setButtonId("");
                                  downloadTemplate(nanobar, _id, `V${versionNum}-${filename}`);
                                }}>Download</li>
                                {versionNum !== 0 && 
                                  <li onClick={() => {
                                    setButtonId("");
                                    setShowDeleteConfirm(true);
                                    setSelectedVersionId(_id);
                                  }}>Delete...</li>
                                }
                              </ul>
                            </div>
                          </div>
                        </li>
                      );
                    })
                  }
                </ul>
              }
              {!versionDataLoading && allVersions.length === 0 && 
                <div className="loading-container vertical">
                  <img src={warning} className="loading" alt="warning"/>
                  <span className="text-left">
                    No version present for this paragraph. 
                    Please upload a paragraph with the same name to create a new version.
                  </span>
                </div>
              }
              {versionDataLoading && 
                <div className="loading-container loading">
                  <img src={refresh} className="loading" alt="loading"/>
                  <span id="loading" className="text-left">Versions loading...</span>
                </div>
              }
            </Fragment>
          )}
          {showDeleteConfirm && 
            <DeleteConfirmModal
              disabeStatus={disabledButton}
              desc={"This will delete the selected version of the paragraph. Once deleted\
              you will not be able to recover it."}
              buttonText="Delete version"
              disable={setDisabledButton}
              close={() => {
                setSelectedVersionId("");
                setShowDeleteConfirm(false);
              }}
              doDelete={() => {
                callDeleteAPI();
              }}
            />
          }
        </div>
      </main>
    </div>
  )
};

export default ParagraphVersion;
